import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBack, Refresh as RefreshIcon, Clear as ClearIcon, Send as SendIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { EndpointSelector } from './EndpointSelector';
import { ResponseViewer } from './ResponseViewer';
import { RequestViewer } from './RequestViewer';
import { ParameterInputs } from './ParameterInputs';
import { RequestBodyEditor } from './RequestBodyEditor';
import { useApiDebug } from '../hooks/useApiDebug';
import { useCurrentUser } from '../../../../hooks/useApi';
import { ApiEndpoint } from '../../../../types/admin';

interface ApiCategoryPageProps {
  categoryName: string;
  categoryDisplayName: string;
  endpoints: ApiEndpoint[];
}

export const ApiCategoryPage: React.FC<ApiCategoryPageProps> = ({ 
  categoryName: _categoryName, 
  categoryDisplayName, 
  endpoints 
}) => {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const {
    selectedEndpoint,
    response,
    request,
    loading,
    error,
    queryParams,
    pathParams,
    requestBody,
    executeEndpoint,
    clearResponse,
    setSelectedEndpoint,
    updateQueryParam,
    updatePathParam,
    updateRequestBody,
  } = useApiDebug();

  useEffect(() => {
    if (currentUser) {
      const hasDevToolsPermission = currentUser.permissions?.includes('developer_tools.view');
      
      if (!hasDevToolsPermission) {
        navigate('/access-denied');
      }
    }
  }, [currentUser, navigate]);

  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  const hasDevToolsPermission = currentUser.permissions?.includes('developer_tools.view');

  if (!hasDevToolsPermission) {
    return null;
  }

  useEffect(() => {
    if (selectedEndpoint && selectedEndpoint.enabled) {
      const hasParams = selectedEndpoint.queryParams?.length || 
                       selectedEndpoint.pathParams?.length || 
                       selectedEndpoint.requestBodyTemplate;
      
      if (!hasParams) {
        executeEndpoint(selectedEndpoint, {}, {}, '');
      }
    }
  }, [selectedEndpoint, executeEndpoint]);

  const handleSendRequest = () => {
    if (selectedEndpoint && selectedEndpoint.enabled) {
      executeEndpoint(selectedEndpoint, queryParams, pathParams, requestBody);
    }
  };

  const handleRefresh = () => {
    if (selectedEndpoint && selectedEndpoint.enabled) {
      executeEndpoint(selectedEndpoint, queryParams, pathParams, requestBody);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            component={RouterLink}
            to="/admin/api-debugging"
            color="inherit"
            underline="hover"
          >
            Dev Tools
          </Link>
          <Typography color="text.primary">{categoryDisplayName}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/api-debugging')}
            variant="outlined"
            size="small"
          >
            Back to Categories
          </Button>
          <Typography variant="h4" component="h1">
            {categoryDisplayName} API Endpoints
          </Typography>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Test {categoryDisplayName} endpoints here. Endpoints without parameters 
          execute automatically when selected. For endpoints with parameters, fill in the required values 
          and click "SEND REQUEST".
        </Alert>

        <Box sx={{ mb: 3 }}>
          <EndpointSelector
            endpoints={endpoints}
            selectedEndpoint={selectedEndpoint}
            onEndpointSelect={setSelectedEndpoint}
            disabled={loading}
          />
        </Box>

        {selectedEndpoint && !selectedEndpoint.enabled && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This endpoint is disabled. Check the endpoint definition for configuration issues.
          </Alert>
        )}

        {selectedEndpoint && selectedEndpoint.enabled && (
          <>
            <ParameterInputs
              title="Path Parameters"
              parameters={selectedEndpoint.pathParams || []}
              values={pathParams}
              onParameterChange={updatePathParam}
              disabled={loading}
            />

            <ParameterInputs
              title="Query Parameters"
              parameters={selectedEndpoint.queryParams || []}
              values={queryParams}
              onParameterChange={updateQueryParam}
              disabled={loading}
            />

            {selectedEndpoint.requestBodyTemplate && (
              <RequestBodyEditor
                requestBody={requestBody}
                onRequestBodyChange={updateRequestBody}
                disabled={loading}
              />
            )}
          </>
        )}

        {selectedEndpoint && selectedEndpoint.enabled && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearResponse}
                disabled={loading}
              >
                Clear
              </Button>
            </Box>
            
            {(selectedEndpoint.queryParams?.length || 
              selectedEndpoint.pathParams?.length || 
              selectedEndpoint.requestBodyTemplate) && (
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendRequest}
                disabled={loading}
                color="primary"
              >
                SEND REQUEST
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <ResponseViewer
          response={response}
          error={error}
          loading={loading}
        />

        <Box sx={{ mt: 3 }}>
          <RequestViewer request={request} />
        </Box>
      </Box>
    </Container>
  );
};